import { useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, ChevronLeft, ChevronRight, SlidersHorizontal, X } from 'lucide-react'
import { searchResources } from '../../services/api'
import { useTaxonomy } from '../../contexts/TaxonomyContext'
import SearchBar from '../../components/search/SearchBar'
import ResultCard from '../../components/search/ResultCard'
import FacetedFilters, { type FilterState } from '../../components/search/FacetedFilters'
import EmbeddingsIndicator from '../../components/search/EmbeddingsIndicator'
import type { SearchParams, SearchResult } from '../../types'

const PAGE_SIZE = 10

function ResultSkeleton() {
  return (
    <div className="rounded-lg border border-gray-200 p-4 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="flex gap-1.5">
            <div className="h-4 w-20 bg-gray-200 rounded" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
          </div>
          <div className="h-5 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="space-y-1.5 mt-2">
            <div className="h-3.5 bg-gray-100 rounded w-full" />
            <div className="h-3.5 bg-gray-100 rounded w-5/6" />
            <div className="h-3.5 bg-gray-100 rounded w-2/3" />
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyResults({ query }: { query: string }) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
        <Search className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">No results found</h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto">
        No resources matched your search for &quot;{query}&quot;. Try adjusting your search terms or filters.
      </p>
    </div>
  )
}

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeTaxonomy } = useTaxonomy()

  const query = searchParams.get('q') || ''
  const embeddingsMode = searchParams.get('embeddings') === 'true'
  const page = parseInt(searchParams.get('page') || '1', 10)

  const [filters, setFilters] = useState<FilterState>({
    taxonomyFilter: undefined,
    resourceTypes: [],
    dateFrom: undefined,
    dateTo: undefined,
    peerReviewed: false,
    audience: [],
  })

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // Build search params for API
  const apiParams = useMemo<SearchParams>(() => ({
    query,
    taxonomy_filter: filters.taxonomyFilter || activeTaxonomy,
    resource_type: filters.resourceTypes.length > 0 ? filters.resourceTypes : undefined,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    peer_reviewed: filters.peerReviewed || undefined,
    audience: filters.audience.length > 0 ? filters.audience : undefined,
    embeddings_mode: embeddingsMode,
    page,
    page_size: PAGE_SIZE,
  }), [query, filters, activeTaxonomy, embeddingsMode, page])

  // Fetch results
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['search', apiParams],
    queryFn: () => searchResources(apiParams),
    enabled: !!query,
    staleTime: 2 * 60 * 1000,
  })

  const results = data?.results || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  // Count cross-domain discoveries
  const crossDomainCount = useMemo(() => {
    return results.filter((r) => r.cross_domain_subjects && r.cross_domain_subjects.length > 0).length
  }, [results])

  // Search handler
  const handleSearch = useCallback(
    (newQuery: string, newEmbeddings: boolean) => {
      setSearchParams({
        q: newQuery,
        embeddings: String(newEmbeddings),
        page: '1',
      })
    },
    [setSearchParams]
  )

  // Pagination
  function goToPage(newPage: number) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('page', String(newPage))
      return params
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Add to list handler (placeholder)
  function handleAddToList(result: SearchResult) {
    // Will be connected to list modal in a future iteration
    console.log('Add to list:', result)
  }

  return (
    <div className="min-h-[calc(100vh-65px)]">
      {/* Top search bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <SearchBar
            initialQuery={query}
            initialEmbeddings={embeddingsMode}
            onSearch={handleSearch}
          />
          {/* Mobile filter toggle */}
          <button
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="lg:hidden flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg transition"
            aria-label="Toggle filters"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Results meta bar */}
        {query && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              {!isLoading && (
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{total.toLocaleString()}</span>
                  {' '}result{total !== 1 ? 's' : ''} found
                  {query && (
                    <span className="text-gray-400">
                      {' '}for &quot;{query}&quot;
                    </span>
                  )}
                </p>
              )}
            </div>
            {embeddingsMode && (
              <EmbeddingsIndicator crossDomainCount={crossDomainCount} />
            )}
          </div>
        )}

        <div className="flex gap-6">
          {/* Left sidebar - filters (desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-20 bg-white rounded-lg border border-gray-200 p-4">
              <FacetedFilters filters={filters} onFilterChange={setFilters} />
            </div>
          </aside>

          {/* Mobile filters overlay */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 z-30 lg:hidden">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setMobileFiltersOpen(false)}
              />
              <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white shadow-xl overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="font-semibold text-gray-900">Filters</h2>
                  <button
                    onClick={() => setMobileFiltersOpen(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <FacetedFilters filters={filters} onFilterChange={setFilters} />
                </div>
              </div>
            </div>
          )}

          {/* Main content area */}
          <main className="flex-1 min-w-0">
            {/* Loading state */}
            {isLoading && (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <ResultSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Error state */}
            {isError && (
              <div className="text-center py-12">
                <p className="text-red-600 font-medium mb-1">Search failed</p>
                <p className="text-sm text-gray-500">
                  {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
                </p>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && !isError && query && results.length === 0 && (
              <EmptyResults query={query} />
            )}

            {/* No query state */}
            {!query && (
              <div className="text-center py-16">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Enter a search query to find academic resources.</p>
              </div>
            )}

            {/* Results list */}
            {!isLoading && results.length > 0 && (
              <div className="space-y-3">
                {results.map((result, index) => (
                  <ResultCard
                    key={`${'record_id' in result ? result.record_id : ('resource_id' in result ? result.resource_id : index)}`}
                    result={result}
                    onAddToList={handleAddToList}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2 mt-8 pb-8" aria-label="Pagination">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    // Show first, last, current, and adjacent pages
                    if (p === 1 || p === totalPages) return true
                    if (Math.abs(p - page) <= 1) return true
                    return false
                  })
                  .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
                    if (i > 0) {
                      const prev = arr[i - 1]
                      if (p - prev > 1) {
                        acc.push('ellipsis')
                      }
                    }
                    acc.push(p)
                    return acc
                  }, [])
                  .map((item, i) => {
                    if (item === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      )
                    }
                    return (
                      <button
                        key={item}
                        onClick={() => goToPage(item)}
                        className={`w-9 h-9 text-sm rounded-md transition ${
                          item === page
                            ? 'bg-acad-500 text-white font-semibold'
                            : 'text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  })}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </nav>
            )}
          </main>
        </div>
      </div>
    </div>
  )
}
